import React from "react"
import PropTypes from "prop-types"
import { GatsbyImage } from "gatsby-plugin-image"
import { Button } from "../Button"
import Link from "../Link"
import Divider from "../Divider"

// 文章含圖片的卡片
export function Card({
  data,
  lgFlex = false,
  lgGrid = false,
  fixedWidth = false,
  isTopic = true,
}) {
  return (
    <div
      className={`text-gray-700 space-y-6 ${
        lgFlex ? "lg:flex lg:justify-start lg:space-y-0 lg:space-x-12" : ""
      } ${lgGrid ? "lg:grid grid-cols-2 lg:space-y-0 lg:gap-12" : ""} ${
        isTopic ? "" : "group"
      }`}
    >
      <Link to={`/post/${data.slug}`}>
        <div className="aspect-video">
          <GatsbyImage
            alt={data.name}
            image={data.image.gatsbyImageData}
            className={`rounded-tr-2xl rounded-bl-2xl w-full h-full ${
              fixedWidth ? "lg:w-96" : "w-full"
            }`}
            imgStyle={{
              borderBottomLeftRadius: "1rem",
              borderTopRightRadius: "1rem",
            }}
          />
        </div>
      </Link>
      {/* Body */}
      <div className="space-y-4 w-full">
        <div className="flex justify-between lg:text-lg">
          {isTopic && (
            <Link
              to={`/${data.topic.slug}`}
              className="text-red-300 hover:text-red-500 transition duration-300 ease-in-out font-bold"
            >
              {data.topic.name}
            </Link>
          )}
          <p className="text-gray-500">{data.updatedAt}</p>
        </div>
        <div>
          <Link to={`/post/${data.slug}`} className="space-y-4">
            <p
              className={`${
                lgGrid ? "text-3xl sm:text-5xl" : "text-3xl"
              } tracking-wider !leading-normal font-bold ${
                isTopic ? "" : "group-hover:underline underline-offset-4"
              }`}
            >
              {data.name}
            </p>
            <p className="text-lg line-clamp-3">{data?.excerpt.excerpt}</p>
          </Link>
        </div>
        <div>
          <Link to={`/post/${data.slug}`}>
            <Button type="ghost" size="none">
              <p className="underline underline-offset-1 text-red-300 hover:text-red-500 duration-300 ease-in-out decoration-red-300">
                繼續閱讀
              </p>
            </Button>
          </Link>
        </div>
      </div>
    </div>
  )
}

// 文章不含圖片的卡片
export function BasicCard({ data }) {
  return (
    <div className="text-gray-700 space-y-4">
      <div className="flex space-x-6 text-lg">
        <Link to={`/${data.topic.slug}`} className="font-bold text-red-300">
          {data.topic.name}
        </Link>
        <p className="text-gray-500">{data.updatedAt}</p>
      </div>
      <Link to={`/post/${data.slug}`}>
        <p className="text-3xl tracking-wide !leading-normal font-bold">
          {data.name}
        </p>
      </Link>
      <Divider />
      <Link to={`/post/${data.slug}`}>
        <p className="text-lg line-clamp-3 pt-4">{data?.excerpt.excerpt}</p>
      </Link>
      <div>
        <Link to={`/post/${data.slug}`}>
          <Button type="ghost" size="none">
            <p className="underline underline-offset-1 text-red-300 decoration-red-300">
              繼續閱讀
            </p>
          </Button>
        </Link>
      </div>
    </div>
  )
}

// 成員含圖片的卡片
export function MemberCard({
  member = {},
  isPosition = false,
  imageClassName = "",
  nameClassName = "",
  titleClassName = "",
  positionClassName = "",
  memberClassName = "",
  className = "block",
  children: licenseNumber,
}) {
  return (
    <div className={`${className}`}>
      <div className={`mx-auto w-48 sm:w-60 ${imageClassName}`}>
        {member.image.gatsbyImageData ? (
          <GatsbyImage
            alt={member.name}
            image={member.image.gatsbyImageData}
            className="rounded-tr-2xl rounded-bl-2xl w-full"
            style={{ paddingTop: "80%" }}
            imgStyle={{
              borderBottomLeftRadius: "1rem",
              borderTopRightRadius: "1rem",
            }}
          />
        ) : (
          <img
            src={member.image}
            alt={member.name}
            className="h-80 object-cover lg:h-[400px] rounded-tr-2xl rounded-bl-2xl w-full"
          />
        )}
      </div>
      <div className={`${memberClassName}`}>
        <p
          className={`mt-3 sm:mt-5 text-center text-2xl sm:text-4xl leading-tight tracking-wider font-bold text-gray-700 transition duration-150 ease-in-out group-hover:text-red-300 ${nameClassName}`}
        >
          {member.name}
        </p>
        {member.title && (
          <p
            className={`mt-1 text-xl sm:text-2xl text-center tracking-wider font-bold text-gray-700 transition duration-150 ease-in-out group-hover:text-red-300 ${titleClassName}`}
          >
            {`擁抱心理${member.title}`}
          </p>
        )}
        <p
          className={`${
            isPosition ? "block" : "hidden"
          } mt-1 text-xl sm:text-2xl text-center tracking-wider font-bold text-gray-700 transition duration-150 ease-in-out group-hover:text-red-300 ${positionClassName}`}
        >
          {member.position}
        </p>
        {licenseNumber}
      </div>
    </div>
  )
}

Card.propTypes = {
  data: PropTypes.object.isRequired,
  lgFlex: PropTypes.bool,
  lgGrid: PropTypes.bool,
  fixedWidth: PropTypes.bool,
  isTopic: PropTypes.bool,
}

BasicCard.propTypes = {
  data: PropTypes.object.isRequired,
}

MemberCard.propTypes = {
  member: PropTypes.object.isRequired,
  isPosition: PropTypes.bool,
  nameClassName: PropTypes.string,
  imageClassName: PropTypes.string,
  titleClassName: PropTypes.string,
  positionClassName: PropTypes.string,
  memberClassName: PropTypes.string,
  className: PropTypes.string,
  children: PropTypes.any,
}
